import { isDefined } from "@omni/utils/helper.utils";
import {
    RefObject,
    TouchEvent,
    TransitionEvent,
    useEffect,
    useRef,
    useState
} from "react";

const TRANSITION_DURATION = "0.2s";

type UseSwipeActionProps = {
    elementRef: RefObject<HTMLElement | null>;
    treshold?: number;
    action: () => void | Promise<void>;
};

export const useSwipeAction = ({
    elementRef,
    treshold = 0.3,
    action
}: UseSwipeActionProps) => {
    const [tresholdReached, setTresholdReached] = useState<boolean>(false);

    const transformRef = useRef<number>(0);
    const previousClientXRef = useRef<number | null>(null);
    const actionScheduledRef = useRef<boolean>(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!transformRef.current) return;

        try {
            navigator.vibrate?.(20);
        } catch {}
    }, [tresholdReached]);

    const hasReachedTreshold = () => {
        if (!elementRef.current) return false;

        const itemWidth = elementRef.current.clientWidth;
        const transformedWidth = transformRef.current;

        if (itemWidth && transformedWidth) {
            return Math.abs(transformedWidth / itemWidth) >= treshold;
        } else {
            return false;
        }
    };

    const handleOnTouchStart = (event: TouchEvent<HTMLElement>) => {
        if (elementRef.current) {
            elementRef.current.style.transitionDuration = "0s";
        }

        previousClientXRef.current = event.touches.item(0).clientX ?? null;
    };

    const handleOnTouchMove = (event: TouchEvent<HTMLElement>) => {
        const currentClientX = event.touches.item(0).clientX ?? null;
        const previousClientX = previousClientXRef.current;

        if (isDefined(currentClientX) && isDefined(previousClientX)) {
            const movement = currentClientX - previousClientX;

            const newTransform = Math.min(transformRef.current + movement, 0);

            transformRef.current = newTransform;

            if (elementRef.current) {
                elementRef.current.style.transform = `translateX(${newTransform}px)`;

                setTresholdReached(hasReachedTreshold());
            }
        }

        previousClientXRef.current = currentClientX;
    };

    const handleOnTouchEnd = (_event: TouchEvent<HTMLElement>) => {
        if (elementRef.current) {
            elementRef.current.style.transitionDuration = TRANSITION_DURATION;

            if (hasReachedTreshold()) {
                elementRef.current.style.transform = `translateX(-100%)`;
                actionScheduledRef.current = true;
            } else {
                elementRef.current.style.transform = `translateX(0)`;
            }
        }

        transformRef.current = 0;
        previousClientXRef.current = null;
    };

    const handleOnTransitionEnd = (_event: TransitionEvent) => {
        if (!actionScheduledRef.current) return;

        actionScheduledRef.current = false;
        action();
    };

    return {
        handlers: {
            onTouchStart: handleOnTouchStart,
            onTouchMove: handleOnTouchMove,
            onTouchEnd: handleOnTouchEnd,
            onTransitionEnd: handleOnTransitionEnd
        },
        tresholdReached: tresholdReached
    };
};
